import type { DashboardSurface } from "./dashboardRoutes";

import { z } from "zod";

import { getDashboardSurface } from "./dashboardRoutes";

export const LAST_VISITED_WORKSPACE_ID_KEY =
  "replo.workspace.lastvisitedWorkspaceId";
export const LAST_VISITED_PROJECT_ID_KEY = "replo.project.lastVisitedProjectId";
export const LAST_VISITED_DASHBOARD_ROUTE_COOKIE = "replo_dashboard_route";

const DASHBOARD_ROUTE_HINT_MAX_AGE_SECONDS = 60 * 60 * 24 * 7;
const dashboardRouteHintSchema = z.object({
  projectId: z.uuid(),
  surface: z.enum(["v1", "v2", "agent"]),
});

const parseAndValidateUuid = (value: string | null): string | null => {
  const result = z.uuid().safeParse(value);
  return result.success ? result.data : null;
};

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && Boolean(window.localStorage);
}

function canUseDocument(): boolean {
  return typeof document !== "undefined";
}

export function clearLastVisitedDashboardRoute() {
  if (!canUseDocument()) {
    return;
  }
  document.cookie = `${LAST_VISITED_DASHBOARD_ROUTE_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
}

export function setLastVisitedDashboardRoute({
  projectId,
  surface,
}: {
  projectId: string;
  surface: DashboardSurface;
}) {
  if (!canUseDocument()) {
    return;
  }
  const result = dashboardRouteHintSchema.safeParse({ projectId, surface });
  if (!result.success) {
    clearLastVisitedDashboardRoute();
    return;
  }
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${LAST_VISITED_DASHBOARD_ROUTE_COOKIE}=${result.data.surface}:${result.data.projectId}; path=/; max-age=${DASHBOARD_ROUTE_HINT_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

export function setLastVisitedDashboardRouteForProject({
  projectId,
  hostingType,
  agentEnabledAt,
}: {
  projectId: string;
  hostingType: string | null;
  agentEnabledAt: Date | string | null;
}) {
  setLastVisitedDashboardRoute({
    projectId,
    surface: getDashboardSurface({ hostingType, agentEnabledAt }),
  });
}

// (Noah, 2025-10-23): Callers that feed these into user.get should read once
// per navigation decision (mount / login), not reactively mid-session — a
// reactive read would refetch the user and flash loading states.
export function getLastVisitedIdsFromStorage(): {
  lastVisitedWorkspaceId: string | null;
  lastVisitedProjectId: string | null;
} {
  if (!canUseLocalStorage()) {
    return { lastVisitedWorkspaceId: null, lastVisitedProjectId: null };
  }
  return {
    lastVisitedWorkspaceId: parseAndValidateUuid(
      window.localStorage.getItem(LAST_VISITED_WORKSPACE_ID_KEY),
    ),
    lastVisitedProjectId: parseAndValidateUuid(
      window.localStorage.getItem(LAST_VISITED_PROJECT_ID_KEY),
    ),
  };
}

// Persist as soon as a project route is chosen. Do not wait on the project
// query — dashboard landing only needs the id, and a delayed write loses the
// preference if the user navigates to "/" before that fetch settles.
export function setLastVisitedProjectId(projectId: string) {
  // Clear the hint until this project's surface has been validated.
  clearLastVisitedDashboardRoute();
  if (!canUseLocalStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(LAST_VISITED_PROJECT_ID_KEY, projectId);
  } catch {
    // Private mode / quota — landing falls back to workspace / oldest project.
  }
}

export function clearLastVisitedWorkspaceId() {
  clearLastVisitedDashboardRoute();
  if (!canUseLocalStorage()) {
    return;
  }
  try {
    window.localStorage.removeItem(LAST_VISITED_WORKSPACE_ID_KEY);
    window.localStorage.removeItem(LAST_VISITED_PROJECT_ID_KEY);
  } catch {
    // ignore
  }
}
